/**
 * usePurchaseStatus — read `?purchase=success|cancelled` off the Stripe
 * Checkout return URL (phase 10 §C). checkout.ts redirects to
 * `SITE_URL/?purchase=success&entitlement=<id>` / `?purchase=cancelled`,
 * so the param lands on the Landing route today; /pricing handles it too
 * in case the return URL moves there later. The param (and the trailing
 * `entitlement`) is scrubbed from history immediately so a refresh
 * doesn't re-announce the purchase.
 */
import { useCallback, useEffect, useState } from "react";

export type PurchaseStatus = "success" | "cancelled";

export function usePurchaseStatus(): {
  purchase: PurchaseStatus | null;
  dismiss: () => void;
} {
  const [purchase, setPurchase] = useState<PurchaseStatus | null>(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const p = params.get("purchase");
      if (p !== "success" && p !== "cancelled") return;
      setPurchase(p);
      params.delete("purchase");
      params.delete("entitlement");
      const qs = params.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`
      );
    } catch {
      // URL/history unavailable — the banner just won't show
    }
  }, []);

  const dismiss = useCallback(() => setPurchase(null), []);
  return { purchase, dismiss };
}
